<template>
  <div class="account">
    <page-header title="Account" />

    <form @submit.prevent="updateAccount">
      <section class="account-info">
        <image-upload
          v-model="account.photoUrl"
          name="profile-image"
          label="Profile Image"
          height="110"
          width="110"
        />

        <div class="inputs">
          <text-input
            id="account-displayName"
            v-model.trim="account.displayName"
            label="Display Name"
            maxlength="30"
          />

          <text-input
            id="account-profileLink"
            v-model.trim="account.profileLink"
            type="url"
            maxlength="75"
            label="Profile Link (Instagram, Facebook, Etc.)"
          />

          <div class="button-row">
            <btn :loading="saving" type="submit">
              Save
            </btn>

            <template v-if="hasPasswordLogin">
              <btn v-if="!isEmployee" kind="secondary" @click="changeEmailOpen = true">
                Change Email
              </btn>

              <btn kind="secondary" @click="changePasswordOpen = true">
                Change Password
              </btn>
            </template>
          </div>
        </div>
      </section>
    </form>

    <div v-if="loggedInProvider" class="logged-in-context">
      <icon class="icon" name="check_circle" />
      <span>Signed in with {{ loggedInProvider }}</span>
    </div>

    <template v-if="hasBilling">
      <h4 class="secondary-font">
        Billing
      </h4>

      <template v-if="hasSubscription">
        <div>
          <btn
            kind="secondary"
            :loading="redirecting"
            loading-text="Redirecting"
            @click="manageSubscription"
          >
            Manage Subscription
          </btn>
        </div>

        <br />
      </template>

      <template v-if="hasCollectorsClubSubscription">
        <h6>Collectors Club Premium Member</h6>

        <div>
          <btn
            kind="secondary"
            :loading="redirecting"
            loading-text="Redirecting"
            @click="manageCollectorsClubSubscription"
          >
            Manage Collectors Club Subscription
          </btn>
        </div>

        <br />
      </template>

      <template v-if="hasStripeSubscription">
        <btn
          kind="secondary"
          :loading="applyingGiftCard"
          loading-text="Applying"
          @click="giftCardEntryOpen = true"
        >
          Apply Gift Card
        </btn>

        <br />
      </template>

      <br />
    </template>

    <h4 class="secondary-font">
      Display Settings
    </h4>

    <form v-if="loaded">
      <select-input
        v-model="account.locale"
        label="Currency"
        :options="currencyOptions"
        @input="updateLocale"
      />

      <checkbox
        v-model="account.enableFuzzinessSearch"
        label="Enable search history spell checker"
        @input="updateAccount"
      />

      <br />

      <checkbox
        v-model="account.hideSalesHistoryValue"
        label="Hide CL Value on Sales History"
        @input="updateAccount"
      />
    </form>

    <br />

    <notification-settings />

    <br />

    <h4 class="secondary-font">
      User ID
    </h4>

    <align>
      <span>{{ user.uid }}</span>

      <icon-button
        tooltip="Copy to clipboard"
        icon="content_copy"
        @click="copyUid"
      />
    </align>

    <div class="advanced">
      <h4 class="secondary-font">
        Advanced
      </h4>

      <div class="button-row">
        <btn v-if="!hasPasswordLogin" kind="secondary" @click="addEmailLoginOpen = true">
          Add Email Login
        </btn>

        <btn kind="secondary" @click="deleteAccount">
          Delete Account
        </btn>
      </div>
    </div>

    <br />

    <h4 class="secondary-font">
      Legal
    </h4>

    <external-link :href="tosLink">
      <span>Terms of Use</span>
    </external-link>

    <br />

    <external-link :href="privacyLink">
      <span>Privacy Policy</span>
    </external-link>

    <modal
      title="Change Email"
      :open="changeEmailOpen"
      @close="changeEmailOpen = false"
    >
      <p class="description">
        Enter a new email and save. This will update your login email and your billing email.
      </p>

      <form @submit.prevent="changeEmail">
        <text-input
          id="account-email"
          v-model.trim="email"
          type="email"
          label="Email"
          required
        />

        <div class="button-row">
          <btn :loading="changingEmail" type="submit">
            Save
          </btn>
          <btn kind="secondary" @click="changeEmailOpen = false">
            Cancel
          </btn>
        </div>
      </form>
    </modal>

    <modal
      title="Change Password"
      :open="changePasswordOpen"
      @close="changePasswordOpen = false"
    >
      <p class="description">
        Enter a new password below to update it and login.
      </p>

      <form @submit.prevent="changePassword">
        <password-input
          id="currentPassword"
          v-model="currentPassword"
          label="Current Password"
          required
        />

        <password-input
          id="newPassword"
          v-model="newPassword"
          label="New Password"
          required
        />

        <div class="button-row">
          <btn :loading="changingPassword" type="submit">
            Save
          </btn>
          <btn kind="secondary" @click="changePasswordOpen = false">
            Cancel
          </btn>
        </div>
      </form>
    </modal>

    <modal
      title="Add Email Login"
      :open="addEmailLoginOpen"
      @close="addEmailLoginOpen = false"
    >
      <p class="description">
        Add an email/password login to your existing account
      </p>

      <form @submit.prevent="addEmailLogin">
        <text-input
          v-model.trim="addEmailLoginEmail"
          type="email"
          label="Email"
          required
        />

        <password-input
          v-model="addEmailLoginPassword"
          label="Password"
          required
        />

        <div class="button-row">
          <btn :loading="addingEmailLogin" type="submit">
            Save
          </btn>
          <btn kind="secondary" @click="addEmailLoginOpen = false">
            Cancel
          </btn>
        </div>
      </form>
    </modal>

    <gift-card-entry
      :open="giftCardEntryOpen"
      @close="giftCardEntryOpen = false"
      @submit="applyGiftCard"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import clone from 'lodash.clonedeep';
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

import { authService } from '@/services';

import NotificationSettings from './NotificationSettings';
import GiftCardEntry from './GiftCardEntry';

export default {
  components: {
    NotificationSettings,
    GiftCardEntry,
  },

  data() {
    return {
      loaded: false,
      account: {},
      changePasswordOpen: false,
      changeEmailOpen: false,
      currentPassword: '',
      newPassword: '',
      email: '',
      saving: false,
      redirecting: false,
      changingEmail: false,
      changingPassword: false,
      applyingGiftCard: false,
      giftCardEntryOpen: false,
      addEmailLoginOpen: false,
      addingEmailLogin: false,
      addEmailLoginEmail: '',
      addEmailLoginPassword: '',
      hasPasswordLogin: true,
      currencyMap: {},
      tosLink: `${import.meta.env.VITE_DOMAIN}/tos`,
      privacyLink: `${import.meta.env.VITE_DOMAIN}/privacy`,
    };
  },

  created() {
    this.load();
  },

  mounted() {
    const currentUser = firebase.auth().currentUser;

    if (currentUser && currentUser.providerData && currentUser.providerData.length > 0) {
      this.hasPasswordLogin = currentUser.providerData.some(({ providerId }) => providerId === 'password');
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    ...mapGetters({
      isMember: 'isMember',
      isEmployee: 'isEmployee',
    }),

    hasBilling() {
      return this.user && this.isMember && (this.user.customerId || this.user.psaCustomerId || (this.user.subscription && this.user.subscription.managementUrl));
    },

    hasSubscription() {
      return this.user.customerId || (this.user.subscription && this.user.subscription.managementUrl);
    },

    hasCollectorsClubSubscription() {
      return this.user.psaCustomerId && this.user.subscription && this.user.subscription.type === 'collectorsclub';
    },

    hasStripeSubscription() {
      return this.user.customerId && this.user.subscription && (this.user.subscription.type === 'monthly' || this.user.subscription.type === 'annual');
    },

    loggedInProvider() {
      if (!this.user || !this.user.uid) {
        return null;
      }

      if (this.user.provider === 'google.com') {
        return 'Google';
      }

      if (this.user.provider === 'apple.com') {
        return 'Apple';
      }

      if (this.user.email) {
        return this.user.email;
      }

      return 'email and password';
    },

    currencyOptions() {
      return Object.keys(this.currencyMap).map((localeKey) => {
        return {
          label: `${this.currencyMap[localeKey].label} (${this.currencyMap[localeKey].symbol})`,
          value: localeKey,
          sort: this.currencyMap[localeKey].sort,
        };
      }).sort((a, b) => a.sort - b.sort);
    },
  },

  methods: {
    async load() {
      this.currencyMap = await authService.getCurrencyMap();

      const user = clone(this.user);

      this.$set(this.account, 'displayName', user.displayName || '');
      this.$set(this.account, 'profileLink', user.profileLink || '');
      this.$set(this.account, 'photoUrl', user.photoUrl || '');
      this.$set(this.account, 'locale', user.locale || 'en-us');
      this.$set(this.account, 'enableFuzzinessSearch', user.enableFuzzinessSearch || false);
      this.$set(this.account, 'hideSalesHistoryValue', user.hideSalesHistoryValue || false);

      this.email = user.email;
      this.loaded = true;
    },

    async updateAccount() {
      this.saving = true;

      try {
        await authService.updateUser(this.user.uid, this.account);
        this.$toasted.success('Your account has been updated!');
      } catch (e) {
        this.$toasted.error('Error updating your account');
        Sentry.captureException(e);
      }

      this.saving = false;
    },

    async updateLocale() {
      try {
        await this.updateAccount();

        this.$store.commit('UPDATE_USER_CURRENCY', this.currencyMap);
      } catch (e) {
        Sentry.captureException(e);
      }
    },

    async changeEmail() {
      this.changingEmail = true;

      try {
        await firebase.functions().httpsCallable('httpUpdateUserEmail')({ uid: this.user.uid, email: this.email });
        this.$toasted.success('Your email has been changed!');
        this.$store.commit('UPDATE_USER', { email: this.email });
      } catch (e) {
        this.$toasted.error(e.message);
        Sentry.captureException(e);
      }

      this.changingEmail = false;
      this.changeEmailOpen = false;
    },

    async changePassword() {
      this.changingPassword = true;

      try {
        await firebase.auth().signInWithEmailAndPassword(this.user.email, this.currentPassword);
        await firebase.auth().currentUser.updatePassword(this.newPassword);
        this.$toasted.success('Your password has been changed!');
      } catch (e) {
        this.$toasted.error(e.message);
        Sentry.captureException(e);
      }

      this.changingPassword = false;
      this.currentPassword = '';
      this.newPassword = '';
      this.changePasswordOpen = false;
    },

    async addEmailLogin() {
      this.addingEmailLogin = true;

      try {
        const credential = firebase.auth.EmailAuthProvider.credential(this.addEmailLoginEmail, this.addEmailLoginPassword);

        await firebase.auth().currentUser.linkWithCredential(credential);

        this.hasPasswordLogin = true;
        this.$toasted.success('Your email and password has been added to your account!');
      } catch (e) {
        this.$toasted.error(e.message);
        Sentry.captureException(e);
      }

      this.addingEmailLogin = false;
      this.addEmailLoginEmail = '';
      this.addEmailLoginPassword = '';
      this.addEmailLoginOpen = false;
    },

    manageSubscription() {
      if (this.user.customerId) {
        this.stripeManagement();
      } else {
        window.open(this.user.subscription.managementUrl, '_blank');
      }
    },

    manageCollectorsClubSubscription() {
      window.open('https://www.psacard.com/myaccount', '_blank');
    },

    async stripeManagement() {
      this.redirecting = true;

      try {
        const { data: session } = await firebase.functions().httpsCallable('httpStripeAccountSession')({
          customerId: this.user.customerId,
          returnUrl: `${import.meta.env.VITE_DOMAIN}/account`,
        });

        window.location.href = session.url;
      } catch (e) {
        this.$toasted.error('Error managing your subscription');
        Sentry.captureException(e);
      }

      this.redirecting = false;
    },

    async applyGiftCard(giftCard) {
      this.applyingGiftCard = true;

      try {
        await firebase.functions().httpsCallable('httpStripeApplyCoupon')({ uid: this.user.uid, coupon: giftCard.stripeCoupon });
        await firebase.functions().httpsCallable('httpGiftUpGiftCardRedeem')({ code: giftCard.code });

        this.$toasted.success('Gift card applied! Click Manage Subscription to verify it worked.');
      } catch (e) {
        this.$toasted.error('There was an error applying your gift card');
      }

      this.applyingGiftCard = false;
    },

    async deleteAccount() {
      if (window.confirm('Are you sure you want to delete your account? All your information will be deleted and you won\'t be able to login anymore.')) {
        if (window.confirm('If you have a subscription through Apple or Google you will need to cancel that yourself. All other subscriptions will be cancelled automatically. Are you sure you still want to delete your account?')) {
          try {
            await authService.deleteAccount(this.user.uid);
            this.$store.dispatch('logout');
            await firebase.auth().signOut();
            window.location.replace(import.meta.env.VITE_LANDING);
          } catch (e) {
            this.$toasted.error('There was an error deleting your account');
          }
        }
      }
    },

    copyUid() {
      this.$copyText(this.user.uid);
      this.$toasted.success('Your User ID has been copied to your clipboard');
    },
  },
};
</script>

<style lang="scss" scoped>
.account {
  @include container;
}

.account-info {
  display: flex;
  flex-direction: column;
  align-items: center;

  @media($small-up) {
    flex-direction: row;
    align-items: flex-start;

    > .image-upload {
      margin-right: $gp;
    }
  }
}

.advanced {
  margin: $gp * 2 0 $gp;
}

form {
  --image-upload-min-height: 184px;

  @media($medium-up) {
    max-width: 640px;
  }
}

.inputs {
  flex: 1;

  @media($medium-down) {
    width: 100%;
  }
}

.logged-in-context {
  display: flex;
  align-items: center;
  margin-bottom: $gp * 2;

  span {
    font-weight: bold;
    margin-left: 5px;
  }

  .icon {
    color: $color-green-1;
  }
}
</style>
